import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { buildApiUrl } from '../config/api';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';

const DemoContext = createContext();

export const useDemo = () => {
  const context = useContext(DemoContext);
  if (!context) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return context;
};

export const DemoProvider = ({ children }) => {
  const { getAuthHeaders } = useAuth();
  const { refresh: refreshSubscription } = useSubscription();
  const [isDemoModeActive, setIsDemoModeActive] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  // Load state from session storage on mount
  useEffect(() => {
    const savedDemoMode = sessionStorage.getItem('demoModeActive');
    const savedProfile = sessionStorage.getItem('selectedDemoProfile');
    
    if (savedDemoMode === 'true') {
      setIsDemoModeActive(true);
    }
    if (savedProfile) {
      setSelectedProfile(savedProfile);
    }
  }, []);

  // Fetch demo profiles from backend
  const fetchProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const res = await fetch(buildApiUrl('/api/demo/profiles'), {
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        throw new Error('Failed to fetch demo profiles');
      }
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setProfiles(data.data);
      }
    } catch (error) {
      console.error('Error fetching demo profiles:', error);
      // Set default profiles if fetch fails
      setProfiles([
        { id: 'profile1', name: 'Early in Career (22-30)', description: 'Just started working...', ageRange: '22-30' },
        { id: 'profile2', name: 'Family Setup (30-40)', description: 'Married without children...', ageRange: '30-40' },
        { id: 'profile3', name: 'Family Growth (40-50)', description: 'Parents with two schooling children...', ageRange: '40-50' },
        { id: 'profile4', name: 'Retirement Mode (50-60)', description: 'College going kids...', ageRange: '50-60' }
      ]);
    } finally {
      setLoadingProfiles(false);
    }
  };

  // Fetch profiles on mount
  useEffect(() => {
    fetchProfiles();
  }, []);

  const toggleDemoMode = () => {
    const newState = !isDemoModeActive;
    setIsDemoModeActive(newState);
    sessionStorage.setItem('demoModeActive', newState.toString());
    
    if (!newState) {
      // Clear selected profile when turning off demo mode
      setSelectedProfile(null);
      sessionStorage.removeItem('selectedDemoProfile');
      
      // Refresh subscription status when returning to normal mode
      // This ensures upgrade banner is displayed correctly
      if (refreshSubscription) {
        refreshSubscription();
      }
    } else if (!selectedProfile && profiles.length > 0) {
      // Auto-select first profile if none selected
      selectProfile(profiles[0].id);
    }
  };

  const selectProfile = (profileId) => {
    setSelectedProfile(profileId);
    sessionStorage.setItem('selectedDemoProfile', profileId);
    
    // Ensure demo mode is active when selecting a profile
    if (!isDemoModeActive) {
      setIsDemoModeActive(true);
      sessionStorage.setItem('demoModeActive', 'true');
    }
  };

  const getDemoQueryParams = () => {
    if (!isDemoModeActive || !selectedProfile) {
      return '';
    }
    return `?demo=true&profile=${selectedProfile}`;
  };

  const getSelectedProfileInfo = () => {
    if (!selectedProfile) return null;
    return profiles.find(p => p.id === selectedProfile) || null;
  };

  const value = useMemo(() => ({
    isDemoModeActive,
    selectedProfile,
    profiles,
    loadingProfiles,
    toggleDemoMode,
    selectProfile,
    getDemoQueryParams,
    getSelectedProfileInfo,
    refreshProfiles: fetchProfiles,
  }), [isDemoModeActive, selectedProfile, profiles, loadingProfiles]);

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
};

